import { QueryDocumentSnapshot } from 'firebase/firestore';
import { Asset } from '@/types/resources/asset';
import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  limit,
  startAfter,
  QueryConstraint,
  writeBatch,
  arrayUnion,
  setDoc
} from 'firebase/firestore';
import { addWorkspaceAndActivity, createDetailedActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { User } from '@/types/users';
import { removeUndefined } from '@/utils/removeUndefined';
import { formatFirestoreTimestamp } from '@/utils/date';
import { loadMockData } from '@/mock';
import { mockWorkspaceStats } from './workspaces';

// Constants
const ASSETS_COLLECTION = 'assets';

// Types
export interface AssetsQueryParams {
  searchTerm?: string;
  status?: string;
  category?: string;
  subCategory?: string;
  type?: string;
  page?: number,
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot;
  firestoreUser: User;
}

export interface CreateAssetInput extends Omit<Asset, 'id' | 'activityRecords' | 'workspace'> {
  // Add any specific fields needed for asset creation
}

// Helper for demo mode pagination
const getPaginatedMockAssets = (
  allAssets: Asset[],
  page: number = 1,
  pageSize: number = 10,
  filters: { searchTerm?: string; status?: string; category?: string; subCategory?: string; type?: string; }
) => {
  let filteredAssets = allAssets;

  if (filters.searchTerm) {
    filteredAssets = filteredAssets.filter(asset =>
      asset.name.toLowerCase().includes(filters.searchTerm!.toLowerCase())
    );
  }
  if (filters.status) {
    filteredAssets = filteredAssets.filter(asset => asset.status === filters.status);
  }
  if (filters.category) {
    filteredAssets = filteredAssets.filter(asset => asset.category === filters.category);
  }
  // Add other filters as needed...

  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const paginatedAssets = filteredAssets.slice(start, end);

  return {
    assets: paginatedAssets,
    total: mockWorkspaceStats.totalAssets || allAssets.length, // Return total from stats for UI consistency
  };
};


// Asset API
export async function getAssets({
  searchTerm,
  status,
  category,
  subCategory,
  type,
  page = 1,
  pageSize = 10,
  lastDoc,
  firestoreUser
}: AssetsQueryParams): Promise<{ assets: Asset[]; lastDoc?: QueryDocumentSnapshot, total?: number }> {
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockAssets = await loadMockData<Asset[]>('assets', firestoreUser.language);
    const { assets, total } = getPaginatedMockAssets(mockAssets, page, pageSize, { searchTerm, status, category });
    return { assets, total };
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const assetsRef = collection(db, ASSETS_COLLECTION);
    const constraints: QueryConstraint[] = [
      where('workspace.id', '==', firestoreUser.workspace.id)
    ];

    // Add search constraints if searchTerm is provided
    if (searchTerm) {
      constraints.push(
        where('name', '>=', searchTerm),
        where('name', '<=', searchTerm + '\uf8ff')
      );
    }

    // Add filter constraints only if explicitly provided
    if (status && status !== '') {
      constraints.push(where('status', '==', status));
    }
    if (category && category !== '') {
      constraints.push(where('category', '==', category));
    }
    if (subCategory && subCategory !== '') {
      constraints.push(where('subCategory', '==', subCategory));
    }
    if (type && type !== '') {
      constraints.push(where('type', '==', type));
    }

    // Add pagination
    if (pageSize) {
      constraints.push(limit(pageSize));
    }
    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    // Build and execute query
    const q = query(assetsRef, ...constraints);
    const snapshot = await getDocs(q);
    const assets = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        purchaseDate: formatFirestoreTimestamp(data.purchaseDate),
        warranty: data.warranty ? {
          ...data.warranty,
          startDate: formatFirestoreTimestamp(data.warranty.startDate),
          endDate: formatFirestoreTimestamp(data.warranty.endDate)
        } : undefined
      } as Asset;
    });

    // Get total count for pagination
    const countQuery = query(collection(db, ASSETS_COLLECTION), where('workspace.id', '==', firestoreUser.workspace.id));
    const countSnapshot = await getDocs(countQuery);
    const total = countSnapshot.size;

    return {
      assets,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total
    };
  } catch (error) {
    console.error('Error fetching assets:', error);
    throw new Error('Failed to fetch assets');
  }
}

export async function getAssetById(id: string, firestoreUser: User): Promise<Asset | null> {
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Use imported mock data
    const mockAssets = await loadMockData<Asset[]>('assets', firestoreUser.language);
    const asset = mockAssets.find(a => a.id === id) || null;
    return asset;
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the asset document directly by ID
    const assetRef = doc(db, ASSETS_COLLECTION, id);
    const assetDoc = await getDoc(assetRef);

    if (!assetDoc.exists()) {
      return null;
    }

    const assetData = assetDoc.data();

    // Check if the asset belongs to the current workspace
    if (assetData.workspace.id !== firestoreUser.workspace.id) {
      return null;
    }

    return {
      ...assetData,
      id: assetDoc.id,
      purchaseDate: formatFirestoreTimestamp(assetData.purchaseDate),
      warranty: assetData.warranty ? {
        ...assetData.warranty,
        startDate: formatFirestoreTimestamp(assetData.warranty.startDate),
        endDate: formatFirestoreTimestamp(assetData.warranty.endDate)
      } : undefined
    } as Asset;
  } catch (error) {
    console.error('Error fetching asset:', error);
    throw new Error('Failed to fetch asset');
  }
}

export async function addAsset(
  asset: CreateAssetInput,
  firestoreUser: User
): Promise<string> {
  try {
    const assetData = addWorkspaceAndActivity(asset, firestoreUser, 'created');

    // Create asset record
    const assetRef = doc(collection(db, ASSETS_COLLECTION));
    await setDoc(assetRef, removeUndefined(assetData));

    return assetRef.id;
  } catch (error) {
    console.error('Error adding asset:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to add asset');
  }
}

export async function updateAsset(
  id: string,
  data: Partial<Asset>,
  firestoreUser: User
): Promise<void> {
  try {
    const docRef = doc(db, ASSETS_COLLECTION, id);
    const assetDoc = await getDoc(docRef);

    if (!assetDoc.exists()) {
      throw new Error('Asset not found');
    }

    const oldAssetData = assetDoc.data() as Asset;
    const activityRecord = createDetailedActivityRecord('updated', oldAssetData, data, firestoreUser);

    // Remove activityRecords from data to avoid conflicts
    const { activityRecords, ...updateData } = data;

    // Prepare the update data without activityRecords
    const cleanData = removeUndefined(updateData);

    // Add activity record separately
    const updatePayload = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating asset:', error);
    throw new Error('Failed to update asset');
  }
}

export async function deleteAsset(
  id: string,
  firestoreUser: User
): Promise<void> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the asset document directly
    const assetRef = doc(db, ASSETS_COLLECTION, id);
    const assetDoc = await getDoc(assetRef);

    if (!assetDoc.exists()) {
      throw new Error('Asset not found');
    }

    const assetData = assetDoc.data();
    if (assetData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Asset not found in current workspace');
    }

    const activityRecord = createDetailedActivityRecord('deleted', assetData, {}, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(assetRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    // Delete the document
    await deleteDoc(assetRef);
  } catch (error) {
    console.error('Error deleting asset:', error);
    throw new Error('Failed to delete asset');
  }
}

// Additional helper functions
export async function getAssetsByCategory(category: string, firestoreUser: User): Promise<Asset[]> {
  if (isDemoWorkspace(firestoreUser)) {
    // Use imported mock data
    const mockAssets = await loadMockData<Asset[]>('assets', firestoreUser.language);
    return mockAssets.filter(asset => asset.category === category);
  }
  const { assets } = await getAssets({ category, firestoreUser });
  return assets;
}

export async function getAssetsBySubCategory(subCategory: string, firestoreUser: User): Promise<Asset[]> {
  if (isDemoWorkspace(firestoreUser)) {
    // Use imported mock data
    const mockAssets = await loadMockData<Asset[]>('assets', firestoreUser.language);
    return mockAssets.filter(asset => asset.subCategory === subCategory);
  }
  const { assets } = await getAssets({ subCategory, firestoreUser });
  return assets;
}

export async function getAssetsByStatus(status: string, firestoreUser: User): Promise<Asset[]> {
  if (isDemoWorkspace(firestoreUser)) {
    // Use imported mock data
    const mockAssets = await loadMockData<Asset[]>('assets', firestoreUser.language);
    return mockAssets.filter(asset => asset.status === status);
  }
  const { assets } = await getAssets({ status, firestoreUser });
  return assets;
}

// Bulk upload assets using Firestore batch operations
export async function addAssetsBulk(
  assets: Omit<Asset, 'id' | 'activityRecords' | 'workspace'>[], 
  firestoreUser: User,
  onProgress?: (uploaded: number, total: number) => void
): Promise<{ successCount: number; errorCount: number; errors: string[] }> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate progress for demo mode
    for (let i = 0; i < assets.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      onProgress?.(i + 1, assets.length);
    }
    return { successCount: assets.length, errorCount: 0, errors: [] };
  }
  
  try {
    const CHUNK_SIZE = 500; // Firestore batch limit
    const chunks = [];
    
    // Split records into chunks of 500
    for (let i = 0; i < assets.length; i += CHUNK_SIZE) {
      chunks.push(assets.slice(i, i + CHUNK_SIZE));
    }

    let totalUploaded = 0;
    const errors: string[] = [];
    
    // Process each chunk with progress updates
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      const batch = writeBatch(db);
      
      chunk.forEach((assetData) => {
        const assetRef = doc(collection(db, ASSETS_COLLECTION));
        const assetWithMetadata = addWorkspaceAndActivity(assetData, firestoreUser, 'created');
        const cleanedAsset = removeUndefined(assetWithMetadata);
        
        batch.set(assetRef, cleanedAsset);
      });

      try {
        await batch.commit();
        totalUploaded += chunk.length;
        onProgress?.(totalUploaded, assets.length);
      } catch (error) {
        console.error(`Error uploading chunk ${chunkIndex + 1}:`, error);
        errors.push(`Chunk ${chunkIndex + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    const successCount = totalUploaded;
    const errorCount = assets.length - totalUploaded;

    return { successCount, errorCount, errors };
  } catch (error) {
    console.error('Error in bulk upload:', error);
    throw new Error('Failed to upload assets in bulk');
  }
} 